import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  pages: { byId: {}, allIds: [], activePageIds: [] },
  conversations: {
    byId: {},
    listOrder: [],
    cursor: { hasMore: false, before: null },
    filters: { tagIds: [], unreadOnly: false, q: '', pageId: null, status: 'inbox' },
    loading: false,
    error: null,
  },
  messages: {
    byConversationId: {},
    sending: {},
  },
  activeId: null,
  customerByConvId: {},   // { [convId]: { customer, orders, loading } }
  customerDraft: {},      // { [convId]: { name, phone, address, productLines, ... } }
};

function sortByLastMessageAtDesc(byId, ids) {
  return [...ids].sort((a, b) => {
    const ta = new Date(byId[a].lastMessageAt).getTime();
    const tb = new Date(byId[b].lastMessageAt).getTime();
    return tb - ta;
  });
}

const chat = createSlice({
  name: 'Chat',
  initialState,
  reducers: {
    pagesLoaded(state, { payload }) {
      state.pages.byId = {};
      state.pages.allIds = [];
      if (Array.isArray(payload)) {
        for (const p of payload) {
          if (p && p.pageId) {
            state.pages.byId[p.pageId] = p;
            state.pages.allIds.push(p.pageId);
          }
        }
      }
    },
    conversationsLoadStarted(state) {
      state.conversations.loading = true;
      state.conversations.error = null;
    },
    conversationsLoaded(state, { payload }) {
      const { conversations, append, hasMore = false, nextCursor = null } = payload;
      if (!append) {
        state.conversations.byId = {};
        state.conversations.listOrder = [];
      }
      for (const c of conversations) {
        state.conversations.byId[c._id] = c;
        if (!state.conversations.listOrder.includes(c._id)) {
          state.conversations.listOrder.push(c._id);
        }
      }
      state.conversations.listOrder = sortByLastMessageAtDesc(
        state.conversations.byId,
        state.conversations.listOrder
      );
      state.conversations.cursor = { hasMore, before: nextCursor };
      state.conversations.loading = false;
    },
    conversationsLoadFailed(state, { payload }) {
      state.conversations.loading = false;
      state.conversations.error = payload;
    },
    conversationSelected(state, { payload }) {
      state.activeId = payload;
      if (state.conversations.byId[payload]) {
        state.conversations.byId[payload].unreadCount = 0;
      }
    },
    conversationPatched(state, { payload }) {
      const { conversationId, patch } = payload;
      const existing = state.conversations.byId[conversationId];
      if (!existing) return;
      Object.assign(existing, patch);
      const currentStatus = state.conversations.filters.status || 'inbox';
      if (
        existing.isDeleted === true ||
        (existing.status && existing.status !== currentStatus)
      ) {
        state.conversations.listOrder = state.conversations.listOrder.filter(
          (id) => id !== conversationId
        );
        if (state.activeId === conversationId) {
          state.activeId = null;
        }
      } else {
        state.conversations.listOrder = sortByLastMessageAtDesc(
          state.conversations.byId,
          state.conversations.listOrder
        );
      }
    },
    filtersChanged(state, { payload }) {
      state.conversations.filters = { ...state.conversations.filters, ...payload };
    },
    messagesLoadStarted(state, { payload }) {
      const convId = payload;
      if (!state.messages.byConversationId[convId]) {
        state.messages.byConversationId[convId] = { items: [], hasMore: false, oldestSentAt: null, loading: true };
      } else {
        state.messages.byConversationId[convId].loading = true;
      }
    },
    messagesLoaded(state, { payload }) {
      const { conversationId, messages, append, hasMore = false } = payload;
      const slot = state.messages.byConversationId[conversationId] || { items: [], hasMore: false, oldestSentAt: null };
      const merged = append ? [...slot.items, ...messages] : [...messages];
      // Dedup by _id
      const seen = new Set();
      const dedup = [];
      for (const m of merged) {
        if (m && m._id && !seen.has(m._id)) { seen.add(m._id); dedup.push(m); }
      }
      // Sort newest first
      dedup.sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
      state.messages.byConversationId[conversationId] = {
        items: dedup,
        hasMore,
        oldestSentAt: dedup.length ? dedup[dedup.length - 1].sentAt : null,
        loading: false,
      };
    },
    messageReceived(state, { payload }) {
      const { conversationId, message, conversation } = payload;
      let slot = state.messages.byConversationId[conversationId];
      if (!slot) {
        slot = { items: [], hasMore: false, oldestSentAt: null, loading: false };
        state.messages.byConversationId[conversationId] = slot;
      }
      if (!slot.items.find((m) => m._id === message._id)) {
        slot.items.unshift(message);
      }
      let conv = state.conversations.byId[conversationId];
      if (!conv && conversation) {
        state.conversations.byId[conversationId] = conversation;
        if (!state.conversations.listOrder.includes(conversationId)) {
          state.conversations.listOrder = [conversationId, ...state.conversations.listOrder];
        }
        conv = state.conversations.byId[conversationId];
      }
      if (conv) {
        conv.lastMessageAt = message.sentAt || new Date().toISOString();
        conv.lastMessageSnippet = (message.text || '[attachment]').slice(0, 80);
        conv.lastMessageDirection = message.direction;
        if (state.activeId !== conversationId && message.direction === 'in') {
          conv.unreadCount = (conv.unreadCount || 0) + 1;
        }
        state.conversations.listOrder = sortByLastMessageAtDesc(
          state.conversations.byId,
          state.conversations.listOrder
        );
      }
    },
    sendMessageStarted(state, { payload }) {
      const { tempId, conversationId, message } = payload;
      state.messages.sending[tempId] = { conversationId, status: 'sending', error: null };
      const slot = state.messages.byConversationId[conversationId] ||
        { items: [], hasMore: false, oldestSentAt: null, loading: false };
      slot.items = [message, ...slot.items];
      state.messages.byConversationId[conversationId] = slot;
    },
    sendMessageSucceeded(state, { payload }) {
      const { tempId, conversationId, message } = payload;
      delete state.messages.sending[tempId];
      const slot = state.messages.byConversationId[conversationId];
      if (slot) {
        slot.items = slot.items.map((m) => (m._id === tempId ? message : m));
      }
    },
    sendMessageFailed(state, { payload }) {
      const { tempId, conversationId, error } = payload;
      const slot = state.messages.byConversationId[conversationId];
      if (slot) {
        slot.items = slot.items.map((m) =>
          m._id === tempId ? { ...m, status: 'failed', failureReason: error } : m
        );
      }
      if (state.messages.sending[tempId]) {
        state.messages.sending[tempId].status = 'failed';
        state.messages.sending[tempId].error = error;
      }
    },
    orderHistoryLoaded(state, { payload }) {
      const { conversationId, customer, orders } = payload;
      state.customerByConvId[conversationId] = { customer, orders, loading: false };
    },
    orderHistoryLoading(state, { payload }) {
      const convId = payload;
      const slot = state.customerByConvId[convId] || { customer: null, orders: [], loading: true };
      slot.loading = true;
      state.customerByConvId[convId] = slot;
    },
    customerLinked(state, { payload }) {
      const { conversationId, customer } = payload;
      if (state.conversations.byId[conversationId]) {
        state.conversations.byId[conversationId].customerId = customer._id;
      }
      state.customerByConvId[conversationId] = { customer, orders: state.customerByConvId[conversationId]?.orders || [], loading: false };
    },
    customerUnlinked(state, { payload }) {
      const convId = payload;
      if (state.conversations.byId[convId]) state.conversations.byId[convId].customerId = null;
      state.customerByConvId[convId] = { customer: null, orders: [], loading: false };
    },
    draftChanged(state, { payload }) {
      const { conversationId, patch } = payload;
      state.customerDraft[conversationId] = { ...(state.customerDraft[conversationId] || {}), ...patch };
    },
    draftCleared(state, { payload }) {
      delete state.customerDraft[payload];
    },
  },
});

export const {
  pagesLoaded,
  conversationsLoadStarted,
  conversationsLoaded,
  conversationsLoadFailed,
  conversationSelected,
  conversationPatched,
  filtersChanged,
  messagesLoadStarted,
  messagesLoaded,
  messageReceived,
  sendMessageStarted,
  sendMessageSucceeded,
  sendMessageFailed,
  orderHistoryLoaded,
  orderHistoryLoading,
  customerLinked,
  customerUnlinked,
  draftChanged,
  draftCleared,
} = chat.actions;

export default chat.reducer;
